import React, {Fragment, useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {ListProps} from './ListProps'

const StyledBaseContainer = styled.div`
  flex: 1;
  position: relative;
`

const StyledListContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  height: 100%;
  width: 100%;
  overflow-y: scroll;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  box-sizing: border-box;
`

const StyledSectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: auto;
`

const StyledSectionRow = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
`

const StyledItemContainer = styled.div`
  width: 100%;
`

export default function List<T>({
  data,
  numColumns = 6,
  renderItem,
  keyExtractor,
  renderRowSeparation,
  gap = 20,
}: ListProps<T>) {
  const numRows = useMemo(() => Math.ceil(data.length / numColumns), [
    data,
    numColumns,
  ])

  const renderColumns = useCallback(
    (indexRow: number) => {
      const indexStart = indexRow * numColumns
      const indexEnd = (indexRow + 1) * numColumns
      const indexLimit = Math.min(indexEnd, data.length)
      const length = indexLimit - indexStart

      return Array.from({length: numColumns}, (_, indexColumn) => {
        const index = indexStart + indexColumn
        const item = data[index]

        return indexColumn < length ? (
          <StyledSectionColumn key={keyExtractor(item, indexColumn)}>
            <StyledItemContainer>{renderItem(item, index)}</StyledItemContainer>
          </StyledSectionColumn>
        ) : (
          <StyledSectionColumn key={`blank [${indexColumn}]`} />
        )
      })
    },
    [data, numColumns, keyExtractor, renderItem],
  )

  const renderRows = useCallback(
    () =>
      Array.from({length: numRows}, (_, index) => (
        <Fragment key={index}>
          {index > 0 && renderRowSeparation && renderRowSeparation(index)}
          <StyledSectionRow style={{gap}}>
            {renderColumns(index)}
          </StyledSectionRow>
        </Fragment>
      )),
    [numRows, gap, renderColumns, renderRowSeparation],
  )

  return (
    <StyledBaseContainer>
      <StyledListContainer>
        <StyledContainer style={{gap}}>{renderRows()}</StyledContainer>
      </StyledListContainer>
    </StyledBaseContainer>
  )
}
